<template>
  <Modal
    class="modal-dark"
    :onClose="onCloseTimeShiftModal">
    <template slot="header">
      <h4 class="program-title">
        {{program.title}}
        <span v-if="isLive" class="live-badge">
          {{$t('capture.timeline.isLive')}}
        </span>
      </h4>
    </template>
    <div
      v-if="isLive || !isFuture"
      class="video-player-container">
      <PreviewByteArkPlayer
        ref="videoPlayer"
        @ready="onPlayerReady"
        :options="playerOptions"
        :statusMessage="statusPreview" />
      <div
        v-if="isTimeShiftingTab"
        class="overlay-actions">
        <md-button
          @click.stop="onLoadPreviousPeriodClicked"
          :disabled="!isPlayerReady"
          class="md-raised">
          <i class="fas fa-fast-backward"></i> {{$t('timeShift.load3Mins')}}
        </md-button>
      </div>
      <div
        v-if="isDurationTab"
        class="overlay-actions load-more">
        <md-button
          @click.stop="onLoad10MinsClicked"
          :disabled="!isPlayerReady"
          class="md-raised">
          {{$t('timeShift.load10Mins')}} <i class="fas fa-fast-forward"></i>
        </md-button>
      </div>
    </div>
    <div class="notice-container">
      <p class="notice" v-if="isLive"><i class="fas fa-info-circle"></i> {{$t('timeShift.warning.isLive')}}</p>
      <p class="notice future" v-else-if="isFuture"><i class="fas fa-info-circle"></i> {{$t('timeShift.warning.isFuture')}}</p>
    </div>
    <md-tabs
      @md-changed="onTabChanged">
      <md-tab id="tab-time-shifting" :md-label="$t('timeShift.modal.shift.title')">
        <div class="modal-container">
          <p class="fine-tuning-title"><i class="fas fa-tools"></i> {{$t('timeShift.fineTuning.title')}}</p>
          <div class="fine-tuning-tools tool-area">
            <md-button
              @click.stop="onSeekBackward"
              class="md-raised">
              <i class="fas fa-backward"></i> 0.5 {{$t('common:second')}}
            </md-button>
            <md-button
              @click.stop="onSetStartTimeClicked"
              :class="{'md-primary': !isStartTimeSelected}"
              :disabled="!isPlayerReady"
              class="md-raised btn-set-time">
              <i class="fas fa-thumbtack"></i> {{$t('timeShift.setAsStart')}}
            </md-button>
            <md-button
              @click.stop="onSeekForward"
              class="md-raised">
              <i class="fas fa-forward"></i> 0.5 {{$t('common:second')}}
            </md-button>
          </div>
          <div class="run-time-container">
            <div class="schedule-run-time">
              <h5 class="run-time-title">{{$t('timeShift.accurateSchedule')}}</h5>
              <p class="run-time">{{accurateRunTime}}</p>
            </div>
            <div
              class="shifted-run-time">
              <h5 class="run-time-title">{{$t('timeShift.modifiedSchedule')}}</h5>
              <p class="run-time">{{shiftedRunTime}}</p>
            </div>
          </div>
        </div>
      </md-tab>
      <md-tab id="tab-time-duration-offset" :md-label="$t('timeShift.modal.duration.title')">
        <div class="modal-container">
          <p class="fine-tuning-title"><i class="fas fa-tools"></i> {{$t('timeShift.fineTuning.title')}}</p>
          <div class="fine-tuning-tools tool-area">
            <md-button
              @click.stop="onSeekBackward"
              class="md-raised">
              <i class="fas fa-backward"></i> 0.5 {{$t('common:second')}}
            </md-button>
            <md-button
              @click.stop="onSetAsEndClicked"
              :disabled="!isPlayerReady"
              :class="{'md-primary': serverDuration === duration}"
              class="md-raised btn-set-time">
              <i class="fas fa-thumbtack"></i> {{$t('timeShift.setAsEnd')}}
            </md-button>
            <md-button
              @click.stop="onSeekForward"
              class="md-raised">
              <i class="fas fa-forward"></i> 0.5 {{$t('common:second')}}
            </md-button>
          </div>
          <div class="run-time-container">
            <div class="schedule-durations">
              <h5 class="run-time-title">{{$t('timeShift.accurateSchedule')}}</h5>
              <p class="run-time">{{accurateDurationText}}</p>
            </div>
            <div class="shifted-durations">
              <h5 class="run-time-title">{{$t('timeShift.modifiedSchedule')}}</h5>
              <p class="run-time">{{shiftedDurationText}}</p>
            </div>
          </div>
        </div>
      </md-tab>
    </md-tabs>
    <template slot="footer">
      <!-- Please use v-show to avoid transition issue. -->
      <div
        v-show="isTimeShiftingTab"
        class="action-buttons-container">
        <md-button
          @click.stop="onShiftIndividualClicked"
          :disabled="!isStartTimeSelected"
          class="btn-save">
          <i class="fas fa-history"></i> {{$t('timeShift.modal.shift.button.thisProgram')}}
        </md-button>
        <md-button
          @click.stop="onShiftAllClicked"
          :disabled="!isStartTimeSelected"
          class="md-primary md-raised">
          <i class="fas fa-history"></i> {{$t('timeShift.modal.shift.button.fromThisProgram')}}
        </md-button>
      </div>
      <div
        v-show="isDurationTab"
        class="action-buttons-container">
        <md-button
          v-if="isStartTimeSelected"
          @click.stop="onUpdateDuration"
          :disabled="serverDuration === duration"
          class="md-primary md-raised">
          <i class="fas fa-history"></i> {{$t('timeShift.modal.durationAndShift.button')}}
        </md-button>
        <md-button
          v-if="!isStartTimeSelected"
          @click.stop="onUpdateDuration"
          :disabled="serverDuration === duration"
          class="md-primary md-raised">
          <i class="fas fa-history"></i> {{$t('timeShift.modal.duration.button')}}
        </md-button>
      </div>
    </template>
  </Modal>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import { shiftProgram, updateScheduleItem } from '@/api/TimeShift';
import { parseToTime } from '@/utils';
import PreviewByteArkPlayer from '@/components/elements/PreviewByteArkPlayer.vue';
import Modal from '@/components/elements/Modal.vue';
import {
  getScheduleRunTime,
  getAccurateRunTime,
} from '@/utils/timeShift';
import {
  getDifferenceMinutes,
  getDifferenceMs,
  formatMinutesSecondsFromMs,
} from '@/utils/time';
import {
  addMilliseconds,
  isAfter,
  isWithinInterval,
  parseISO,
} from 'date-fns';

export default {
  components: {
    Modal,
    PreviewByteArkPlayer,
  },
  data() {
    return {
      mediaURL: null,
      currentTab: 'tab-time-shifting',
      previousTimeLoaded: 0,
      duration: 0,
      offset: 0,
      serverOffset: 0,
      player: null,
      accurateStartedAt: null,
      accurateEndedAt: null,
      accurateRunTime: null,
      accurateDurationMinutes: 0,
      accurateDurationText: '',
      shiftedDurationText: '',
      serverDuration: 0,
      now: null,
      apiError: false,
      playerOptions: {
        autoplay: true,
        fluid: true,
        aspectRatio: '16:9',
        sources: {
          src: null,
          type: 'application/x-mpegURL',
        },
        transparentContainer: true,
      },
      statusPreview: {
        error: null,
        type: null,
      },
      isStartTimeSelected: false,
      somethingHasChanged: false,
    };
  },
  computed: {
    getScheduleRunTime,
    getAccurateRunTime,
    getDifferenceMinutes,
    ...mapState({
      program: state => state.timeShift.currentTimeShiftProgram,
    }),
    isTimeShiftingTab() {
      return this.currentTab === 'tab-time-shifting';
    },
    isDurationTab() {
      return this.currentTab === 'tab-time-duration-offset';
    },
    msOffset() {
      return this.offset * 1000;
    },
    shiftedStartedAt() {
      if (this.serverOffset !== 0 && this.offset === 0) {
        return addMilliseconds(this.accurateStartedAt, this.serverOffset);
      }
      return addMilliseconds(this.accurateStartedAt, this.msOffset);
    },
    shiftedEndedAt() {
      if (this.serverOffset !== 0 && this.offset === 0) {
        return addMilliseconds(this.accurateEndedAt, this.serverOffset);
      }
      return addMilliseconds(this.accurateEndedAt, this.msOffset);
    },
    shiftedRunTime() {
      return `${parseToTime(this.shiftedStartedAt, true)} - ${parseToTime(this.shiftedEndedAt, true)}`;
    },
    isLive() {
      return isWithinInterval(this.now, { start: this.accurateStartedAt, end: this.accurateEndedAt });
    },
    isFuture() {
      if (isAfter(this.accurateStartedAt, this.now)) {
        return true;
      }
      return isAfter(this.accurateEndedAt, this.now);
    },
    isPlayerReady() {
      if (this.player) {
        return this.player.currentTime() > 0 && this.player.readyState();
      }
      return false;
    },
  },
  created() {
    this.now = new Date();
    // Set initial values
    this.initTimeShiftVariables();
    this.initDurationVariables();
    this.mediaURL = _.get(this.program, 'mediaUrl', null);
    this.playerOptions.sources.src = this.mediaURL;
  },
  methods: {
    initTimeShiftVariables() {
      const [accurateStartedAt, accurateEndedAt] = getAccurateRunTime(this.program, true);
      this.accurateStartedAt = parseISO(accurateStartedAt);
      this.accurateEndedAt = parseISO(accurateEndedAt);
      this.serverOffset = _.get(this.program, 'adjustment.startedAtOffsetMs', 0);
      this.offset = this.serverOffset !== 0 ? this.serverOffset / 1000 : 0;
      this.previousTimeLoaded = this.offset;
    },
    initDurationVariables() {
      const [accurateStartedAt, accurateEndedAt] = getAccurateRunTime(this.program, true);
      this.accurateRunTime = getAccurateRunTime(this.program);
      this.accurateDurationMinutes = getDifferenceMinutes(accurateStartedAt, accurateEndedAt);

      const serverDuration = _.get(this.program, 'adjustment.durationOffsetMs', 0);
      this.serverDuration = serverDuration;
      const accurateDurationMs = getDifferenceMs(accurateStartedAt, accurateEndedAt);
      this.accurateDurationText = formatMinutesSecondsFromMs(accurateDurationMs);
      this.duration = serverDuration !== 0 ? serverDuration : 0;

      if (serverDuration === 0) {
        this.shiftedDurationText = this.accurateDurationText;
      } else {
        this.shiftedDurationText = formatMinutesSecondsFromMs(accurateDurationMs + serverDuration);
      }
    },
    setPlayerSource(src) {
      if (!this.isFuture) {
        this.player.src({ src });

        const isPlaying = this.player.currentTime() > 0 && !this.player.paused() && !this.player.ended()
          && this.player.readyState();
        if (!isPlaying) {
          this.player.play();
        }
      }
    },
    onSeekBackward() {
      this.player.pause();
      const currentTime = this.player.currentTime();
      if (currentTime - 0.5 > 0) {
        this.player.currentTime(currentTime - 0.5);
      } else {
        this.player.currentTime(0);
      }
    },
    onSeekForward() {
      this.player.pause();
      const duration = this.player.duration();
      const currentTime = this.player.currentTime();
      if (currentTime + 0.5 < duration) {
        this.player.currentTime(currentTime + 0.5);
      } else {
        this.player.currentTime(currentTime);
      }
    },
    onLoadPreviousPeriodClicked() {
      this.somethingHasChanged = true;
      this.previousTimeLoaded -= 180;
      const milliseconds = this.previousTimeLoaded * 1000;
      this.setPlayerSource(`${this.mediaURL}?startedAtOffsetMs=${milliseconds}`);
    },
    onLoad10MinsClicked() {
      this.somethingHasChanged = true;
      this.duration += 600 * 1000;
      this.setPlayerSource(`${this.mediaURL}?durationOffsetMs=${this.duration}`);

      const totalDuration = this.player.duration();
      const afterAdded = totalDuration + 600;
      const shiftedMinutes = Math.abs(Math.floor(afterAdded / 60));
      const shiftedSeconds = Math.abs(Math.floor(afterAdded % 60));
      let shiftedText = `${shiftedMinutes} ${this.$t('common:minute', { count: shiftedMinutes })}`;
      if (shiftedSeconds !== 0) {
        shiftedText += ` ${shiftedSeconds} ${this.$t('common:second', { count: shiftedSeconds })}`;
      }
      this.shiftedDurationText = shiftedText;
    },
    onSetStartTimeClicked() {
      this.somethingHasChanged = true;
      const currentTime = this.player.currentTime();
      this.previousTimeLoaded += currentTime;
      const milliseconds = this.previousTimeLoaded * 1000;
      this.setPlayerSource(`${this.mediaURL}?startedAtOffsetMs=${milliseconds}`);
      this.offset = this.previousTimeLoaded;
      this.isStartTimeSelected = true;
    },
    onSetAsEndClicked() {
      this.somethingHasChanged = true;
      const totalDuration = this.player.duration();
      const currentTime = this.player.currentTime();
      this.duration += (currentTime - totalDuration) * 1000;
      this.setPlayerSource(`${this.mediaURL}?durationOffsetMs=${this.duration}`);

      const shiftedMinutes = Math.abs(Math.floor(currentTime / 60));
      const shiftedSeconds = Math.abs(Math.floor(currentTime % 60));
      let shiftedText = `${shiftedMinutes} ${this.$t('common:minute', { count: shiftedMinutes })}`;
      if (shiftedSeconds !== 0) {
        shiftedText += ` ${shiftedSeconds} ${this.$t('common:second', { count: shiftedSeconds })}`;
      }
      this.shiftedDurationText = shiftedText;
    },
    onCloseTimeShiftModal() {
      if (this.somethingHasChanged) {
        // eslint-disable-next-line no-restricted-globals,no-alert
        if (confirm('Discard changes and close the modal?')) {
          this.$store.dispatch('resetCurrentTimeShiftProgram');
        }
      } else {
        this.$store.dispatch('resetCurrentTimeShiftProgram');
      }
    },
    onTabChanged(tabName) {
      this.currentTab = tabName;
    },
    onPlayerReady(player) {
      this.player = player;
    },
    async onShiftAllClicked() {
      try {
        await shiftProgram(this.program.channelId, this.program.id, this.program.scheduleDate, {
          startedAtOffsetMs: this.msOffset,
        });
        this.$emit('shift');
      } catch (error) {
        this.apiError = error;
      }
    },
    async onUpdateDuration() {
      try {
        await updateScheduleItem(this.program.id, {
          startedAtOffsetMs: this.msOffset,
          durationOffsetMs: this.duration,
        });
        this.$emit('shift');
      } catch (error) {
        this.apiError = error;
      }
    },
    // async onShiftAndUpdateDuration() {
    //   try {
    //     await updateScheduleItem(this.program.id, {
    //       startedAtOffsetMs: this.msOffset,
    //       durationOffsetMs: this.duration,
    //     });
    //     this.$emit('shift');
    //   } catch (error) {
    //     this.apiError = error;
    //   }
    // },
    async onShiftIndividualClicked() {
      try {
        await updateScheduleItem(this.program.id, {
          startedAtOffsetMs: this.msOffset,
        });
        this.$emit('shift');
      } catch (error) {
        this.apiError = error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';

.program-title {
  margin: 0;
  .live-badge {
    background: $dark-400;
    padding: 4px;
    font-size: 0.6em;
    position: relative;
    top: -2px;
    border-radius: 3px;
  }
}

.video-player-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 280px;
  min-height: 280px;
  background: $dark-lavender;
  color: #FFF;
  > div {
    width: 100%;
    height: 100%;
  }
  .overlay-actions {
    position: absolute;
    z-index: 20;
    height: auto;
    bottom: 66px;
    &.load-more {
      display: flex;
      justify-content: flex-end;
    }
    .md-button {
      opacity: 0.9;
      &:hover {
        opacity: 1;
      }
    }
  }
  .error-container {
    background-color: transparent;
    box-shadow: none;
  }
}

.modal-container {
  margin: 10px 8px 12px;
}

.fine-tuning-title {
  margin: 0;
}

.tool-area {
  display: flex;
  flex-flow: row;
  margin: 10px 0 20px;
  .md-raised {
    margin: 0;
  }
  .btn-set-time {
    flex: 1;
    margin: 0 20px;
  }
}

.run-time-container {
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  .schedule-run-time {
    flex: 0 0 47%;
  }
  .shifted-run-time {
    flex: 0 0 49%;
  }
  .schedule-durations {
    flex: 0 0 48%;
  }
  .shifted-durations {
    flex: 0 0 48%;
  }
  .run-time-title {
    margin: 0;
    font-size: 13px;
  }
}
.shifting-area {
  padding-top: 10px;
}

.action-buttons-container {
  text-align: right;
  .btn-save {
    margin-right: 4px;
  }
}

.notice-container {
  text-align: center;
  background-color: #3f454a;
  .notice {
    padding: $spacer;
    margin: 0;
    &.future {
      padding: 80px $spacer;
    }
  }
}

// Override Modal configurations
::v-deep .modal-box {
  .close-button {
    top: 1.05em;
  }
  .modal-container-footer {
    background-color: $dark-active-tab;
    margin-top: -20px;
    padding: 0 16px 20px;
  }
}

// Override Material Tab configurations
::v-deep .md-tabs {
  .md-content.md-theme-default {
    background-color: $dark-active-tab;
  }
  &.md-theme-default {
    .md-tabs-navigation {
      .md-button-content {
        font-size: 1.1em;
      }
      .md-button {
        width: 50%;
        &.md-active {
          background-color: $dark-active-tab;
          .md-button-content {
            font-weight: bold;
          }
          &:focus {
            outline: 0;
          }
        }
      }
    }
  }
}
::v-deep .md-tabs-indicator {
  top: 0;
  bottom: unset;
}
</style>
